import { slicedToArray as _slicedToArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useState, useMemo, useEffect, Fragment } from 'react';
import { i18n } from '../../../i18n/translate.js';
import { ErrorPage } from '../../ErrorPage/index.js';
import { ChartExportModal } from '../ui-components/chart-export-modal.js';
import { ChartToolbar } from '../ui-components/chart-toolbar.js';
import { storage } from '../../../utils/Storage.js';
import isNil from 'lodash/isNil';
import { hasCapability } from '../../../utils/AccessControlUtil.js';
import { toggleElementFullscreen } from '../../../utils/toggleElementFullscreen.js';
import { JsonApiRecordDecorator } from '../../../json-api/JsonApiRecordDecorator.js';
import { format } from '../../../utils/dateHelpers.js';

var TableauCloudChart = function TableauCloudChart(props) {
  var context = props.context,
      _props$tableau = props.tableau,
      tableau = _props$tableau === void 0 ? window.tableauV3 : _props$tableau,
      capabilities = props.capabilities,
      _props$showReportTool = props.showReportToolbar,
      showReportToolbar = _props$showReportTool === void 0 ? false : _props$showReportTool,
      padding = props.padding,
      _props$toolbarType = props.toolbarType,
      toolbarType = _props$toolbarType === void 0 ? 'standard' : _props$toolbarType,
      currentEntity = props.currentEntity,
      onError = props.onError,
      createDataExport = props.createDataExport,
      filters = props.filters,
      shortDataExportPoll = props.shortDataExportPoll,
      longDataExportPoll = props.longDataExportPoll,
      errorMessage = props.errorMessage;
  var chartRef = useRef(null);
  var initialPollingRef = useRef();
  var longPollingRef = useRef();
  var vizRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      pollingExport = _useState2[0],
      setPollingExport = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showExportModal = _useState4[0],
      setShowExportModal = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      error = _useState6[0],
      setError = _useState6[1];

  var _useState7 = useState(null),
      _useState8 = _slicedToArray(_useState7, 2),
      activeSheet = _useState8[0],
      setActiveSheet = _useState8[1];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      workbook = _useState10[0],
      setWorkbook = _useState10[1];

  var cleanedFilters = useMemo(function () {
    if (!filters) return [];
    return Object.entries(filters).filter(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2);

      _ref2[0];
      var value = _ref2[1];
      return value !== null && value !== undefined;
    });
  }, [filters]);

  var clearPollIntervals = function clearPollIntervals() {
    clearInterval(initialPollingRef.current);
    clearInterval(longPollingRef.current);
    initialPollingRef.current = undefined;
    longPollingRef.current = undefined;
  };

  useEffect(function () {
    if (isNil(tableau)) {
      setError(i18n.t('There was an error while loading Tableau, try refreshing the page.'));
      return;
    }

    if (!chartRef.current) return;
    if (chartRef.current.querySelector('tableau-viz')) return;

    try {
      var TableauViz = tableau.TableauViz,
          TableauEventType = tableau.TableauEventType,
          Toolbar = tableau.Toolbar;
      var viz = new TableauViz();
      viz.src = context === null || context === void 0 ? void 0 : context.iframe_src;
      viz.token = context === null || context === void 0 ? void 0 : context.tableau_key;
      viz.toolbar = Toolbar.Hidden;
      cleanedFilters.forEach(function (k, v) {
        viz.addFilter(k, v);
      });
      viz.addEventListener(TableauEventType.FirstInteractive, function () {
        setWorkbook(viz.workbook);
        setActiveSheet(viz.workbook.activeSheet);
      });
      vizRef.current = viz;
      chartRef.current.appendChild(viz);
    } catch (e) {
      setError(i18n.t('Unable to connect to tableau.'));
    }
  }, [context, tableau, cleanedFilters]);
  useEffect(function () {
    if (!activeSheet || !filters) return;
    var FilterUpdateType = tableau.FilterUpdateType; // Apply all passed filters with valid values for each worksheet

    cleanedFilters.forEach(function (_ref3) {
      var _activeSheet$workshee;

      var _ref4 = _slicedToArray(_ref3, 2),
          key = _ref4[0],
          value = _ref4[1];

      activeSheet === null || activeSheet === void 0 || (_activeSheet$workshee = activeSheet.worksheets) === null || _activeSheet$workshee === void 0 || _activeSheet$workshee.forEach(function (worksheet) {
        worksheet.applyFilterAsync(key, [value], FilterUpdateType.Replace);
      });
    });
  }, [activeSheet, cleanedFilters, tableau]);
  useEffect(function () {
    if (!currentEntity) return;
    var status = currentEntity.status;

    if (['completed', 'error', 'completed_without_download'].includes(status)) {
      setPollingExport(false);
      clearPollIntervals();
    }

    if (status === 'error' || errorMessage) {
      setPollingExport(false);
      setShowExportModal(false);
    }
  }, [currentEntity, pollingExport]);
  useEffect(function () {
    var noPollIntervals = isNil(initialPollingRef.current) && isNil(longPollingRef.current);
    var shouldPoll = (currentEntity === null || currentEntity === void 0 ? void 0 : currentEntity.id) && pollingExport && noPollIntervals;

    if (shouldPoll) {
      var setPolls = /*#__PURE__*/function () {
        var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
          return _regeneratorRuntime().wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return shortDataExportPoll(currentEntity.id);

                case 2:
                  initialPollingRef.current = _context.sent;
                  _context.next = 5;
                  return longDataExportPoll(currentEntity.id);

                case 5:
                  longPollingRef.current = _context.sent;

                case 6:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        return function setPolls() {
          return _ref5.apply(this, arguments);
        };
      }();

      setPolls();
    }
  }, [currentEntity, pollingExport]);
  useEffect(function () {
    return function () {
      clearPollIntervals();
    };
  }, []);

  if (error) {
    return /*#__PURE__*/React.createElement(ErrorPage, {
      renderAsLegacy: true,
      errors: [error],
      title: i18n.t('Something Went Wrong.')
    });
  }

  var canDownloadReport = !capabilities || hasCapability('Capabilities::FileDownloads', capabilities);
  var style = padding ? {
    padding: padding
  } : undefined;
  var downloadModalTitle = currentEntity !== null && currentEntity !== void 0 && currentEntity.download_url ? i18n.t('Report Ready') : i18n.t('Generating your report');

  var revertAll = /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (!workbook) {
                _context2.next = 5;
                break;
              }

              _context2.next = 3;
              return workbook.revertAllAsync();

            case 3:
              _context2.next = 6;
              break;

            case 5:
              if (vizRef.current) {
                setWorkbook(vizRef.current.workbook);
                vizRef.current.workbook.revertAllAsync();
              } else {
                setError(i18n.t('No workbook or Viz found.'));
              }

            case 6:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function revertAll() {
      return _ref6.apply(this, arguments);
    };
  }(); // TODO: hugo.sanchez - Disabled until figure out refreshing not working in V3
  // const refreshData = async () => {
  //   await vizRef.current.refreshDataAsync();
  // };


  var toggleFullscreen = function toggleFullscreen() {
    toggleElementFullscreen(chartRef.current);
  };

  var toolbarButtons = [{
    onClick: revertAll,
    icon: 'xmark',
    label: i18n.t('Clear Filters')
  }, // TODO: hugo.sanchez - Disabled until figure out refreshing not working in V3
  // {
  //   onClick: refreshData,
  //   icon: 'undo',
  //   label: i18n.t('Refresh Data'),
  //   hide: context.chart.disable_refresh,
  // },
  {
    onClick: toggleFullscreen,
    icon: 'expand',
    label: i18n.t('Full Screen')
  }];

  var mapSheetFilters = function mapSheetFilters(acc, filter) {
    var FilterType = tableau.FilterType;

    if (filter && filter.filterType === FilterType.Categorical && !filter.isAllSelected && !['Measure Names', 'Date Range Filter'].includes(filter.fieldName) && filter.appliedValues) {
      acc[filter.fieldName] = filter.appliedValues.map(function (val) {
        var _val$formattedValue;

        return (_val$formattedValue = val.formattedValue) === null || _val$formattedValue === void 0 ? void 0 : _val$formattedValue.replaceAll(',', '\\,');
      }).join(',');
    }

    return acc;
  };

  var getFilters = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4(sheets) {
      return _regeneratorRuntime().wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              if (sheets !== null && sheets !== void 0 && sheets.length) {
                _context4.next = 2;
                break;
              }

              return _context4.abrupt("return", {});

            case 2:
              return _context4.abrupt("return", sheets.map( /*#__PURE__*/function () {
                var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(sheet) {
                  var sheetFilters;
                  return _regeneratorRuntime().wrap(function _callee3$(_context3) {
                    while (1) {
                      switch (_context3.prev = _context3.next) {
                        case 0:
                          _context3.next = 2;
                          return sheet.getFiltersAsync();

                        case 2:
                          sheetFilters = _context3.sent;
                          return _context3.abrupt("return", sheetFilters.reduce(mapSheetFilters, {}));

                        case 4:
                        case "end":
                          return _context3.stop();
                      }
                    }
                  }, _callee3);
                }));

                return function (_x2) {
                  return _ref8.apply(this, arguments);
                };
              }()));

            case 3:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));

    return function getFilters(_x) {
      return _ref7.apply(this, arguments);
    };
  }();

  var mapWorkbookParameters = function mapWorkbookParameters(acc, param) {
    var DataType = tableau.DataType;

    if (param && param.dataType === DataType.Date) {
      acc[param.name.replace(/\s/g, '')] = format(param.currentValue.formattedValue, 'yyyy-MM-dd');
    }

    return acc;
  };

  var getParameters = /*#__PURE__*/function () {
    var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee5(workbook) {
      var parameters;
      return _regeneratorRuntime().wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              if (workbook) {
                _context5.next = 2;
                break;
              }

              return _context5.abrupt("return", {});

            case 2:
              _context5.next = 4;
              return workbook.getParametersAsync();

            case 4:
              parameters = _context5.sent;
              return _context5.abrupt("return", parameters.reduce(mapWorkbookParameters, {}));

            case 6:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5);
    }));

    return function getParameters(_x3) {
      return _ref9.apply(this, arguments);
    };
  }();

  var exportData = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee6() {
      var _context$chart, csv_view, csv_workbook, sheets, exportFilters, exportParameters, newDataExport;

      return _regeneratorRuntime().wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              _context$chart = context === null || context === void 0 ? void 0 : context.chart, csv_view = _context$chart.csv_view, csv_workbook = _context$chart.csv_workbook;
              sheets = activeSheet.worksheets;
              _context6.next = 4;
              return getFilters(sheets);

            case 4:
              exportFilters = _context6.sent;
              _context6.next = 7;
              return getParameters(workbook);

            case 7:
              exportParameters = _context6.sent;
              newDataExport = Object.assign(new JsonApiRecordDecorator(), {
                type: 'data_exports',
                data_type: 'Datafi::Exports::TableauCloud',
                name: workbook.name,
                filters: {
                  filters: Object.assign({}, exportFilters, exportParameters),
                  view_name: String(csv_view),
                  workbook_name: String(csv_workbook)
                }
              });
              setShowExportModal(true);
              setPollingExport(true);
              createDataExport(newDataExport);

            case 12:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6);
    }));

    return function exportData() {
      return _ref10.apply(this, arguments);
    };
  }();

  var exportCrossTab = /*#__PURE__*/function () {
    var _ref11 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee7() {
      var CrosstabFileFormat, message;
      return _regeneratorRuntime().wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              CrosstabFileFormat = tableau.CrosstabFileFormat;
              _context7.prev = 1;
              _context7.next = 4;
              return vizRef.current.exportCrosstabAsync(context.chart.csv_view, CrosstabFileFormat.CSV);

            case 4:
              _context7.next = 10;
              break;

            case 6:
              _context7.prev = 6;
              _context7.t0 = _context7["catch"](1);
              message = _context7.t0 === null || _context7.t0 === void 0 ? void 0 : _context7.t0.message;
              onError(i18n.t("Unable to download, please contact an admin (".concat(message, ")")));

            case 10:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7, null, [[1, 6]]);
    }));

    return function exportCrossTab() {
      return _ref11.apply(this, arguments);
    };
  }();

  var exportImage = /*#__PURE__*/function () {
    var _ref12 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee8() {
      var message;
      return _regeneratorRuntime().wrap(function _callee8$(_context8) {
        while (1) {
          switch (_context8.prev = _context8.next) {
            case 0:
              _context8.prev = 0;
              _context8.next = 3;
              return vizRef.current.exportImageAsync();

            case 3:
              _context8.next = 9;
              break;

            case 5:
              _context8.prev = 5;
              _context8.t0 = _context8["catch"](0);
              message = _context8.t0 === null || _context8.t0 === void 0 ? void 0 : _context8.t0.message;
              onError(i18n.t("Unable to export, please contact an admin (".concat(message, ")")));

            case 9:
            case "end":
              return _context8.stop();
          }
        }
      }, _callee8, null, [[0, 5]]);
    }));

    return function exportImage() {
      return _ref12.apply(this, arguments);
    };
  }();

  var exportPDF = /*#__PURE__*/function () {
    var _ref13 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee9() {
      var message;
      return _regeneratorRuntime().wrap(function _callee9$(_context9) {
        while (1) {
          switch (_context9.prev = _context9.next) {
            case 0:
              _context9.prev = 0;
              _context9.next = 3;
              return vizRef.current.exportPDFAsync();

            case 3:
              _context9.next = 9;
              break;

            case 5:
              _context9.prev = 5;
              _context9.t0 = _context9["catch"](0);
              message = _context9.t0 === null || _context9.t0 === void 0 ? void 0 : _context9.t0.message;
              onError(i18n.t("Unable to download, please contact an admin (".concat(message, ")")));

            case 9:
            case "end":
              return _context9.stop();
          }
        }
      }, _callee9, null, [[0, 5]]);
    }));

    return function exportPDF() {
      return _ref13.apply(this, arguments);
    };
  }();

  var onSelectDownload = function onSelectDownload(downloadType) {
    switch (downloadType) {
      case 'workbook':
        exportCrossTab();
        break;

      case 'tableau_api':
        exportData();
        break;

      case 'image':
        exportImage();
        break;

      case 'pdf':
        exportPDF();
        break;
    }
  };

  var closeExportModal = function closeExportModal() {
    clearPollIntervals();
    setShowExportModal(false);
    setPollingExport(false);
  };

  var onDownloaded = function onDownloaded() {
    storage.setItem('suppressLoadingClass', 'true');
  };

  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(ChartToolbar, {
    buttons: toolbarButtons,
    canDownloadReport: canDownloadReport,
    exportAvailability: context.chart.export_availability,
    onSelectDownload: onSelectDownload,
    pollingExport: pollingExport,
    show: showReportToolbar,
    style: style,
    toolbarType: toolbarType
  }), /*#__PURE__*/React.createElement("div", {
    className: "chart",
    ref: chartRef
  }), /*#__PURE__*/React.createElement(ChartExportModal, {
    downloadUrl: currentEntity === null || currentEntity === void 0 ? void 0 : currentEntity.download_url,
    onClose: closeExportModal,
    onDownloadClicked: onDownloaded,
    show: showExportModal,
    title: downloadModalTitle
  }));
};

TableauCloudChart.displayName = "TableauCloudChart";
export { TableauCloudChart };